/**
 * TotalsSummary - Shared component for displaying totals with tax calculation
 * Used in estimates and invoices create/edit modals
 * 
 * Supports decimal quantities (e.g., 1.5 hours)
 */

import { Link } from "react-router-dom";

export default function TotalsSummary({
  items,
  taxRatePercent = 0,
  showSettingsLink = true,
}) {
  // Calculate subtotal - supports decimal quantities
  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      // Parse quantity as float, default to 1 if empty/invalid
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
  };

  // Calculate taxable subtotal - supports decimal quantities
  const calculateTaxableTotal = (items) => {
    return items.reduce((sum, item) => {
      if (!item.is_taxable) return sum;
      // Parse quantity as float, default to 1 if empty/invalid
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
  };

  // Calculate tax amount
  const calculateTax = (items) => {
    const taxableTotal = calculateTaxableTotal(items);
    return taxableTotal * (taxRatePercent / 100);
  };

  const subtotal = calculateTotal(items);
  const taxableAmount = calculateTaxableTotal(items);
  const tax = calculateTax(items);
  const total = subtotal + tax;

  return (
    <div className="border-t border-zinc-200 pt-4 space-y-2">
      <div className="flex justify-between items-center text-sm">
        <span>Subtotal:</span>
        <span className="font-medium">${subtotal.toFixed(2)}</span>
      </div>
      {taxRatePercent > 0 && (
        <div className="flex justify-between items-center text-sm">
          <span>Tax ({taxRatePercent}% on ${taxableAmount.toFixed(2)}):</span>
          <span className="font-medium">${tax.toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between items-center text-lg border-t border-zinc-200 pt-2">
        <span className="font-semibold">Total:</span>
        <span className="font-heading text-2xl font-bold">${total.toFixed(2)}</span>
      </div>
      {taxRatePercent === 0 && showSettingsLink && (
        <p className="text-xs text-zinc-500">
          No tax rate configured. <Link to="/settings" className="text-[#FF6600]">Set up in Settings</Link>
        </p>
      )}
    </div>
  );
}
